import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { showToastMessage } from '../../../../components'
import type { DropdownOption } from '../../../../components/Dropdown'
import {
  downloadBlob,
  editAttendanceRecord,
  exportAttendanceRecords,
  getAttendanceRecords,
  getDepartments,
  type BackendAttendanceRecord,
  type BackendAttendanceStatus,
  type EditAttendancePayload,
} from '../services/attendanceService'
import { useDepartmentsQuery } from '../../../User-Role-Management/Department/hooks/useDepartment'
import type { RegularizationRequest } from '../types/hrmsAttendanceDetails.types'
import type {
  StaffAttendanceRecord,
  StaffAttendanceStatus,
  StaffAttendanceSummary,
} from '../types/hrmsAttendanceRecords.types'

export const PAGE_SIZE = 20

function parseTimeToMinutes(timeStr?: string | null): number | null {
  if (!timeStr || timeStr === 'none' || timeStr === '—') return null
  const clean = timeStr.replace(/am|pm|\s/gi, '')
  const [hStr, mStr = '0'] = clean.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (isNaN(h) || isNaN(m)) return null
  let totalH = h
  if (/pm/i.test(timeStr) && h < 12) totalH += 12
  if (/am/i.test(timeStr) && h === 12) totalH = 0
  return totalH * 60 + m
}

export function mapBackendRecordToStaffRecord(
  item: BackendAttendanceRecord
): StaffAttendanceRecord {
  const statusMap: Record<string, StaffAttendanceStatus> = {
    PRESENT: 'Present',
    LATE: 'Late',
    ABSENT: 'Absent',
    HALF_DAY: 'Half Day',
    ON_LEAVE: 'On Leave',
    Present: 'Present',
    Late: 'Late',
    Absent: 'Absent',
    'Half Day': 'Half Day',
    'On Leave': 'On Leave',
  }

  let workingMinutes = 0
  if (typeof item.workingHours === 'number') {
    workingMinutes =
      item.workingHours <= 24
        ? Math.round(item.workingHours * 60)
        : Math.round(item.workingHours)
  } else if (typeof item.workingHours === 'string') {
    const num = parseFloat(item.workingHours)
    if (!isNaN(num)) {
      workingMinutes = num <= 24 ? Math.round(num * 60) : Math.round(num)
    }
  } else if (item.checkInTime && item.checkOutTime) {
    const inMin = parseTimeToMinutes(item.checkInTime)
    const outMin = parseTimeToMinutes(item.checkOutTime)
    if (inMin !== null && outMin !== null && outMin > inMin) {
      workingMinutes = outMin - inMin
    }
  }

  const staff = item.staff
  const staffFullName = [staff?.firstName, staff?.lastName].filter(Boolean).join(' ').trim()
  const resolvedName = staff?.name || staffFullName || 'Unknown Staff'
  const resolvedEmployeeId = staff?.employeeId || staff?.code || item.staffId || '-'
  const resolvedDesignation = staff?.designationName || staff?.code || '-'
  const resolvedDepartment = staff?.departmentName || '-'

  return {
    id: item.id,
    name: resolvedName,
    designation: resolvedDesignation,
    employeeId: resolvedEmployeeId,
    staffId: item.staffId || (staff as any)?.id,
    department: resolvedDepartment,
    date: item.attendanceDate,
    checkIn: item.checkInTime || undefined,
    checkOut: item.checkOutTime || undefined,
    workingMinutes,
    status: statusMap[item.status] || 'Present',
    source: (item.source as any) || 'Biometric',
    rawRecord: item,
  }
}

export function mapStatusToBackendQuery(
  status: string
): BackendAttendanceStatus | undefined {
  switch (status) {
    case 'Present':
    case 'PRESENT':
      return 'PRESENT'
    case 'Late':
    case 'LATE':
      return 'LATE'
    case 'Absent':
    case 'ABSENT':
      return 'ABSENT'
    case 'Half Day':
    case 'HALF_DAY':
      return 'HALF_DAY'
    case 'On Leave':
    case 'ON_LEAVE':
      return 'ON_LEAVE'
    default:
      return undefined
  }
}

interface UseAttendanceRecordsParams {
  selectedDate: string
  selectedDepartment: string
  onDateChange: (val: string) => void
  onDepartmentChange: (val: string) => void
  disabled?: boolean
}

export function useAttendanceRecords({
  selectedDate,
  selectedDepartment,
  onDateChange,
  onDepartmentChange,
  disabled = false,
}: UseAttendanceRecordsParams) {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('All Statuses')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [isExporting, setIsExporting] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [records, setRecords] = useState<StaffAttendanceRecord[]>([])
  const [total, setTotal] = useState<number>(0)
  const [summary, setSummary] = useState<StaffAttendanceSummary>({
    present: 0,
    late: 0,
    absent: 0,
  })
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false)

  // Department dropdown options via shared React Query hook
  const { data: deptItems = [], isLoading: isDepartmentsLoading } = useDepartmentsQuery(undefined, true, !disabled)
  const departmentOptions: DropdownOption[] = useMemo(() => {
    return [
      { label: 'All Departments', value: '' },
      ...deptItems.map((d) => ({
        label: d.departmentName || (d as any).name || 'Unnamed Department',
        value: d.id,
      })),
    ]
  }, [deptItems])

  // Debounce search ~400ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 400)
    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery])

  const closeDetails = useCallback(() => {
    setSelectedRecordId(null)
  }, [])

  const openDetails = useCallback((idOrRecord: string | StaffAttendanceRecord) => {
    if (typeof idOrRecord === 'object' && idOrRecord !== null) {
      setSelectedRecordId(idOrRecord.id || idOrRecord.employeeId)
    } else {
      setSelectedRecordId(idOrRecord)
    }
  }, [])

  // Reset page to 1 when filters change (except currentPage itself)
  useEffect(() => {
    if (disabled) return
    setCurrentPage(1)
    closeDetails()
  }, [selectedDate, selectedDepartment, debouncedSearch, statusFilter, closeDetails, disabled])

  // Fetch records from real API with AbortController to cancel stale/duplicate in-flight calls
  const activeControllerRef = useRef<AbortController | null>(null)

  const fetchRecords = useCallback(async () => {
    if (disabled) {
      setLoading(false)
      return
    }

    if (activeControllerRef.current) {
      activeControllerRef.current.abort()
    }
    const controller = new AbortController()
    activeControllerRef.current = controller

    setLoading(true)
    try {
      const deptId =
        selectedDepartment && selectedDepartment !== 'All Departments'
          ? selectedDepartment.trim()
          : undefined

      const backendStatus = mapStatusToBackendQuery(statusFilter)

      const res = await getAttendanceRecords({
        search: debouncedSearch.trim() || undefined,
        departmentId: deptId,
        status: backendStatus,
        fromDate: selectedDate || undefined,
        toDate: selectedDate || undefined,
        page: currentPage,
        limit: PAGE_SIZE,
      })

      if (!controller.signal.aborted) {
        const mapped = res.items.map(mapBackendRecordToStaffRecord)
        setRecords(mapped)
        setTotal(res.total)

        // Derive stats from current view
        let p = 0
        let l = 0
        let a = 0
        mapped.forEach((r) => {
          if (r.status === 'Present') p++
          else if (r.status === 'Late') l++
          else if (r.status === 'Absent') a++
        })
        setSummary({ present: p, late: l, absent: a })
        return { count: mapped.length, total: res.total }
      }
      return { count: 0, total: 0 }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        const msg =
          err?.response?.data?.message || err?.message || 'Failed to load attendance records'
        showToastMessage(msg, 'error')
        setRecords([])
        setTotal(0)
      }
      return { count: 0, total: 0 }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false)
      }
    }
  }, [debouncedSearch, selectedDepartment, statusFilter, selectedDate, currentPage, disabled])

  useEffect(() => {
    fetchRecords()
    return () => {
      if (activeControllerRef.current) {
        activeControllerRef.current.abort()
      }
    }
  }, [fetchRecords])

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      closeDetails()
    },
    [closeDetails]
  )

  const applyApprovedRegularization = useCallback((req: RegularizationRequest) => {
    setRecords((prevRecords) =>
      prevRecords.map((rec) => {
        if (rec.employeeId !== req.employeeId) return rec

        const newCheckIn = req.requestedCheckIn || rec.checkIn
        const newCheckOut = req.requestedCheckOut || rec.checkOut

        const newInMin = parseTimeToMinutes(newCheckIn)
        const newOutMin = parseTimeToMinutes(newCheckOut)

        let newWorkingMinutes = rec.workingMinutes

        if (newInMin !== null && newOutMin !== null) {
          newWorkingMinutes = Math.max(0, newOutMin - newInMin)
        } else if (rec.workingMinutes !== undefined && rec.checkIn) {
          const oldInMin = parseTimeToMinutes(rec.checkIn)
          if (oldInMin !== null && newInMin !== null) {
            const delta = oldInMin - newInMin
            newWorkingMinutes = Math.max(0, rec.workingMinutes + delta)
          }
        }

        return {
          ...rec,
          checkIn: newCheckIn,
          checkOut: newCheckOut,
          workingMinutes: newWorkingMinutes,
        }
      })
    )
  }, [])

  const selectedRecord = useMemo(() => {
    if (!selectedRecordId) return null
    return (
      records.find((r) => r.id === selectedRecordId || r.employeeId === selectedRecordId) ||
      null
    )
  }, [records, selectedRecordId])

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1
  const from = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const to = Math.min(currentPage * PAGE_SIZE, total)

  const handleClearFilters = useCallback(() => {
    setSearchQuery('')
    setStatusFilter('All Statuses')
    onDepartmentChange('')
    onDateChange('')
    setCurrentPage(1)
    closeDetails()
  }, [onDepartmentChange, onDateChange, closeDetails])

  const openImportModal = useCallback(() => {
    setIsImportModalOpen(true)
  }, [])

  const closeImportModal = useCallback(() => {
    setIsImportModalOpen(false)
  }, [])

  // Edit attendance record (PATCH /records/{id})
  const updateRecordAttendance = useCallback(
    async (recordId: string, updates: EditAttendancePayload) => {
      setIsSaving(true)
      try {
        await editAttendanceRecord(recordId, updates)
        showToastMessage('Attendance updated successfully', 'success')
        await fetchRecords()
        return true
      } catch (err: any) {
        const msg =
          err?.response?.data?.message || err?.message || 'Failed to update attendance'
        showToastMessage(msg, 'error')
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [fetchRecords]
  )

  // Export records (GET /records/export)
  const handleExport = useCallback(async () => {
    setIsExporting(true)
    try {
      const deptId =
        selectedDepartment && selectedDepartment !== 'All Departments'
          ? selectedDepartment.trim()
          : undefined

      const backendStatus = mapStatusToBackendQuery(statusFilter)

      const res = await exportAttendanceRecords({
        search: debouncedSearch.trim() || undefined,
        departmentId: deptId,
        status: backendStatus,
        fromDate: selectedDate || undefined,
        toDate: selectedDate || undefined,
        format: 'xlsx',
      })

      downloadBlob(res.data, res.filename || 'attendance-records.xlsx')
      showToastMessage('Attendance exported successfully', 'success')
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Failed to export attendance records'
      showToastMessage(msg, 'error')
    } finally {
      setIsExporting(false)
    }
  }, [debouncedSearch, selectedDepartment, statusFilter, selectedDate])

  return {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage: handlePageChange,
    loading,
    summary,
    filteredCount: total,
    paginatedRecords: records,
    totalPages,
    from,
    to,
    selectedRecord,
    openDetails,
    closeDetails,
    applyApprovedRegularization,
    handleClearFilters,
    handleExport,
    isExporting,
    handleViewRecord: openDetails,
    isImportModalOpen,
    openImportModal,
    closeImportModal,
    handleImportAttendance: openImportModal,
    updateRecordAttendance,
    isSaving,
    departmentOptions,
    isDepartmentsLoading,
    refetchRecords: fetchRecords,
  }
}
